import { useState } from 'react';
import { STORY_POINTS, effortInput, parseEffortMinutes, storyPointsSymbol } from '../../shared/issuePlanning';
import { Modal, fmtDate } from './components';
import { t } from './i18n';
import type { HistoryRow } from './types';

const CONFIRMED = 40, ASSIGNED = 50, RESOLVED = 80, CLOSED = 90;

/** Calendar time from the first change to "confirmed" to the last change to "resolved", from the status history. */
export interface StatusSpan { minutes: number; from: string; to: string; fromStatus: number; toStatus: number }

export function statusSpan(history: HistoryRow[]): StatusSpan | null {
  const changes = history
    .filter((h) => h.field_name === 'status')
    .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at) || a.id - b.id);
  const into = (status: number) => changes.filter((h) => Number(h.new_value) === status);
  // issues assigned without being confirmed first start when they were assigned
  const start = into(CONFIRMED)[0] ?? into(ASSIGNED)[0];
  // reopened issues count until they were resolved the last time; closed without "resolved" counts until closed
  const end = (into(RESOLVED).length ? into(RESOLVED) : into(CLOSED)).at(-1);
  if (!start || !end) return null;
  const minutes = Math.round((Date.parse(end.created_at) - Date.parse(start.created_at)) / 60_000);
  if (!(minutes > 0)) return null;
  return { minutes, from: start.created_at, to: end.created_at, fromStatus: Number(start.new_value), toStatus: Number(end.new_value) };
}

const duration = (minutes: number) => {
  const days = Math.floor(minutes / 1440);
  const time = effortInput(minutes % 1440);
  return days ? t('{days} d {time} h', { days: String(days), time }) : t('{time} h', { time });
};

/** Popup that suggests the confirmed → resolved time as needed time; the value can be changed before it is used. */
function StatusSpanDialog({ span, statusLabel, onApply, onClose }: {
  span: StatusSpan; statusLabel: (status: number) => string; onApply: (value: string) => void; onClose: () => void;
}) {
  const [value, setValue] = useState(effortInput(span.minutes));
  const [error, setError] = useState('');
  const apply = () => {
    try {
      if (parseEffortMinutes(value) == null) throw new Error();
      onApply(value.trim());
    } catch {
      setError(t('Enter effort as H:MM or decimal hours.'));
    }
  };
  return (
    <Modal title={t('Needed time from the status history')} onClose={onClose} width={520}>
      <form onSubmit={(e) => { e.preventDefault(); apply(); }}>
        <dl className="status-span">
          <dt>{statusLabel(span.fromStatus)}</dt><dd>{fmtDate(span.from)}</dd>
          <dt>{statusLabel(span.toStatus)}</dt><dd>{fmtDate(span.to)}</dd>
          <dt>{t('Calendar time')}</dt><dd>{duration(span.minutes)}</dd>
        </dl>
        <p className="subtle small">{t('This is how long the issue was open, not the working time. Change it if needed.')}</p>
        <label className="field">
          <span className="field-label">{t('Needed time (actual, HH:MM)')}</span>
          <input value={value} onChange={(e) => { setValue(e.target.value); setError(''); }} inputMode="decimal" autoFocus />
          {error && <span className="error small">{error}</span>}
        </label>
        <div className="form-footer">
          <button className="btn primary">{t('Use this time')}</button>
          <button type="button" className="btn subtle" onClick={onClose}>{t('Cancel')}</button>
        </div>
      </form>
    </Modal>
  );
}

export interface PlanningForm { story_points: string; estimated_time: string; needed_time: string }

export function planningPayload(form: PlanningForm, canTime: boolean) {
  try {
    return {
      story_points: form.story_points || null,
      estimated_minutes: parseEffortMinutes(form.estimated_time),
      ...(canTime ? { needed_minutes: parseEffortMinutes(form.needed_time) } : {}),
    };
  } catch { throw new Error(t('Enter effort as H:MM or decimal hours.')); }
}

export function storyPointsText(value: string | null | undefined): string {
  if (value == null) return t('Not set');
  const item = STORY_POINTS.find((option) => option.value === value);
  return item ? `${storyPointsSymbol(item.value)} — ${t(item.label)}` : String(value);
}

/** On the issue page (not editing): a button by the needed time that opens the same popup and saves the result. */
export function NeededTimeFromHistory({ span, statusLabel, onSave, saving }: {
  span: StatusSpan; statusLabel: (status: number) => string; onSave: (minutes: number) => void; saving?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="btn subtle small span-btn" disabled={saving} onClick={() => setOpen(true)}>
        ⏱ {t('Calculate from status history (confirmed → resolved)')}
      </button>
      {open && <StatusSpanDialog span={span} statusLabel={statusLabel} onClose={() => setOpen(false)}
        onApply={(value) => { onSave(parseEffortMinutes(value) ?? 0); setOpen(false); }} />}
    </>
  );
}

export function IssuePlanningFields({ value, onChange, canTime, autoMinutes, span, statusLabel }: {
  value: PlanningForm; onChange: (field: keyof PlanningForm, value: string) => void; canTime: boolean; autoMinutes?: number | null;
  /** offered for issues without any needed time yet (old issues) */
  span?: StatusSpan | null; statusLabel?: (status: number) => string;
}) {
  const selected = STORY_POINTS.find((item) => item.value === value.story_points);
  const [spanOpen, setSpanOpen] = useState(false);
  const offerSpan = canTime && span && statusLabel && !value.needed_time.trim() && !autoMinutes;
  return (
    <fieldset className="issue-planning-fields">
      <legend>{t('Effort & complexity')}</legend>
      <div className="field-row three">
        <label className="field">
          <span className="field-label">{t('Complexity / Story points')}</span>
          <select value={value.story_points} onChange={(event) => onChange('story_points', event.target.value)}>
            <option value="">{t('Not set')}</option>
            {STORY_POINTS.map((item) => <option key={item.value} value={item.value}>{storyPointsSymbol(item.value)} — {t(item.label)}</option>)}
          </select>
          {selected && <span className="subtle small">{t(selected.description)}</span>}
        </label>
        <label className="field">
          <span className="field-label">{t('Estimated time (HH:MM)')}</span>
          <input value={value.estimated_time} onChange={(event) => onChange('estimated_time', event.target.value)} placeholder="2:30" inputMode="decimal" />
        </label>
        {canTime && <label className="field">
          <span className="field-label">{t('Needed time (actual, HH:MM)')}</span>
          <input value={value.needed_time} onChange={(event) => onChange('needed_time', event.target.value)} placeholder={autoMinutes != null ? t('Auto: {time}', { time: effortInput(autoMinutes) }) : t('From time entries')} inputMode="decimal" />
          <span className="subtle small">{t('Leave blank to use logged time. A manual total replaces it; the two are never added together.')}</span>
          {offerSpan && <button type="button" className="btn subtle span-btn" onClick={() => setSpanOpen(true)}>⏱ {t('Calculate from status history (confirmed → resolved)')}</button>}
        </label>}
        {spanOpen && span && statusLabel && <StatusSpanDialog span={span} statusLabel={statusLabel}
          onApply={(v) => { onChange('needed_time', v); setSpanOpen(false); }} onClose={() => setSpanOpen(false)} />}
      </div>
      <p className="subtle small">{t('Story points describe complexity, not hours. Estimates are optional and can change as you learn.')}</p>
      <details className="planning-scale">
        <summary>{t('Estimation scale and meaning')}</summary>
        <dl>{STORY_POINTS.map((item) => <div key={item.value}><dt>{storyPointsSymbol(item.value)} — {t(item.label)}</dt><dd>{t(item.description)}</dd></div>)}</dl>
      </details>
    </fieldset>
  );
}
